import moment from 'moment'
import React, { useCallback, useContext } from 'react'

import LineCommentIcon from 'LuxKit/Icons/line/LineCommentIcon'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import LineRefreshIcon from 'LuxKit/Icons/line/LineRefreshIcon'
import LineSettingIcon from 'LuxKit/Icons/line/LineSettingIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { useAppSelector } from 'hooks/reduxHooks'
import useNavigateTo from 'hooks/useNavigateTo'
import { getCurrentUserId } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { AddRemoveItineraryButton } from 'tripPlanner/components/Itinerary/AddRemoveItineraryButton'
import ItineraryDateButton from 'tripPlanner/components/Itinerary/ItineraryDateButton'
import RegenerateItemModal from 'tripPlanner/components/Itinerary/RegenerateIndividualItem/RegenerateItemModal'
import DeleteTripItem from 'tripPlanner/components/TripModal/TripItems/DeleteTripItem'
import EditTripItem from 'tripPlanner/components/TripModal/TripItems/Edit'
import EditTripDates from 'tripPlanner/components/TripModal/Trips/EditTripDatesTP'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import { ItemMetadata } from 'tripPlanner/models'
import { TripItem } from 'tripPlanner/types/tripItem'
import { getOrderId, isCustomItem, buildMyEscapesOrderLink } from 'tripPlanner/utils'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { canSetItemStartDateFromNoDates } from 'tripPlanner/utils/itinerary'
import { mediaQueryDown } from 'components/utils/breakpoint'
import Divider from 'LuxKit/Divider'
import GroupWithSeparator from 'tripPlanner/components/CleanCard/common/GroupWithSeparator'
import useDesktopWithoutTouch from 'tripPlanner/hooks/useDesktopWithoutTouch'

const FullWidthWrapper = styled.div`
  button {
    width: 100%;
    justify-content: flex-start;
  }
`

const MobileOnlyDivider = styled(Divider)`
  display: none;

  ${mediaQueryDown.mobile} {
    display: block;
  }
`

interface Props {
  item: TripItem
  offerLink?: string
  onRequestAddComment?: () => void
}

export default function ItemMenuOptions(props: Props) {
  const { item, onRequestAddComment, offerLink } = props
  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()
  const navigateTo = useNavigateTo()
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const canShowEdit = isCustomItem(item) && ItemMetadata[item.type].editable

  const orderId = getOrderId(item)
  const userId = useAppSelector(getCurrentUserId)

  const documentsContext = useContext(TripDocumentsContext)

  const canSetDatesFromNoDates = canSetItemStartDateFromNoDates(item)
  const isDesktopWithoutTouch = useDesktopWithoutTouch()

  const onClickMoveTo = useCallback(() => {
    const onRequestAddTripDates = (
      onDatesAdded?: (startDate: moment.Moment, endDate: moment.Moment) => void,
    ) => {
      openModal(EditTripDates, { tripId: item.tripId, onDatesAdded })
    }

    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: tripItemSelectionId(item.id) })
    } else {
      onRequestAddTripDates()
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const onClickAddToItinerary = useCallback(() => {
    if (isCustomItem(item)) {
      openModal(EditTripItem, { item })
    } else if (offerLink) {
      navigateTo(offerLink + '#date-picker')
      setImmersiveMode({ tripId: item.tripId, sectionId: tripItemSelectionId(item.id) })
    }
  }, [item, offerLink, openModal, navigateTo, setImmersiveMode])

  const handleReplaceClick = useCallback(() => {
    openModal(RegenerateItemModal, { item }, { size: 'L', height: 'full', tabletHeight: 'auto' })
  }, [openModal, item])

  const showRegenerateItemButton =
    ('name' in item && item.name) ||
    (
      'place' in item &&
      item.place &&
      'name' in item.place &&
      item.place.name
    )

  const shouldShowReplace = showRegenerateItemButton && !item.isSuggested && item.startDate && canSetDatesFromNoDates

  const handleDeleteItem = useCallback(() => {
    openModal(DeleteTripItem, { item }, { height: 'auto' })
  }, [openModal, item])

  const handleClickAddComment = useCallback(() => {
    closeModal()
    requestAnimationFrame(() => {
      if (onRequestAddComment) {
        onRequestAddComment()
      }
    })
  }, [closeModal, onRequestAddComment])

  const handleClickEdit = useCallback(() => openModal(EditTripItem, { item }), [openModal, item])
  const handleClickAddDocument = useCallback(() => documentsContext?.onRequestAddDocument(item.id), [documentsContext, item.id])

  const isBooked = 'isBooked' in item && item.isBooked

  const myEscapesLink = buildMyEscapesOrderLink(item)

  return (
    <GroupWithSeparator direction="vertical" separator={<MobileOnlyDivider kind="secondary" direction="horizontal" />}>
      <div>
        {canSetDatesFromNoDates && (
          <FullWidthWrapper>
            <ItineraryDateButton
              item={item}
              variant="button"
              onRequestAddTripDates={onClickMoveTo}
            />
          </FullWidthWrapper>
        )}
        {!canSetDatesFromNoDates && (
          <FullWidthWrapper>
            <AddRemoveItineraryButton
              item={item}
              variant="button"
              onClickAddTripItemDates={onClickAddToItinerary}
            />
          </FullWidthWrapper>
        )}

        {orderId && item.createdBy === userId && (
          <ClickableListItem
            startIcon={<LineSettingIcon size="S" />}
            title="Manage booking"
            to={myEscapesLink}
            target="_blank"
          />
        )}
      </div>

      <div>
        {canShowEdit && (
          <ClickableListItem
            startIcon={<LinePenIcon size="S" />}
            onClick={handleClickEdit}
            title="Edit"
          />
        )}
        {shouldShowReplace && (
          <ClickableListItem
            startIcon={<LineRefreshIcon size="S" />}
            onClick={handleReplaceClick}
            title="Replace"
          />
        )}
      </div>

      <div>
        {!!onRequestAddComment && !item.notes && (
          <ClickableListItem
            startIcon={<LineCommentIcon size="S" />}
            onClick={handleClickAddComment}
            title={item.type === 'NOTE' ? 'Add a note' : 'Add a comment'}
          />
        )}
        {!!documentsContext && (
          <ClickableListItem
            startIcon={<LinePaperclipIcon size="S" />}
            onClick={handleClickAddDocument}
            title="Attach a document"
          />
        )}
      </div>
      {(isBooked || !isDesktopWithoutTouch) && (
        <ClickableListItem
          startIcon={<LineTrashAltIcon size="S" colour="critical" />}
          onClick={handleDeleteItem}
          title="Delete from trip"
          titleColour="critical"
        />
      )}
    </GroupWithSeparator>
  )
}
